/**
 * Admin: foreningsregister (seilforeninger).
 *
 * Regattaer, båter og brukere tilhører en forening. På den offentlige siden
 * velger brukeren forening først. «Operatør styrer alle» — admin forvalter alle.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';

export function AdminForeninger() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['foreninger-admin'], queryFn: () => api.listForeningerAdmin() });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['foreninger-admin'] });
    qc.invalidateQueries({ queryKey: ['foreninger'] });
  };
  const create = useMutation({ mutationFn: api.createForening, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: (v: { id: string; data: Parameters<typeof api.updateForening>[1] }) =>
      api.updateForening(v.id, v.data),
    onSuccess: invalidate,
  });
  const del = useMutation({ mutationFn: (id: string) => api.deleteForening(id), onSuccess: invalidate });

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    create.mutate(
      { name: name.trim(), shortName: shortName.trim() || null },
      {
        onSuccess: () => {
          setName('');
          setShortName('');
        },
        onError: (e) => setErr(e instanceof ApiError ? e.message : 'Kunne ikke opprette'),
      },
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold">Foreninger</h1>
        <p className="text-xs text-slate-400 mt-1">
          Seilforeninger. Regattaer, båter og brukere tilhører en forening, og brukeren velger
          forening på forsiden.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 sm:items-end"
      >
        <div>
          <label className="block text-xs text-slate-400 mb-1">Navn</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="f.eks. Arendal Seilforening"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Kortnavn (valgfri)</label>
          <input
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="f.eks. ASF"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
          />
        </div>
        <button
          disabled={name.trim().length < 2 || create.isPending}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold disabled:opacity-40"
        >
          Opprett
        </button>
      </form>
      {err && <p className="text-sm text-red-300">{err}</p>}

      {list.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (
        <ul className="space-y-2">
          {(list.data ?? []).map((f) => (
            <li
              key={f.id}
              className={`bg-slate-900/50 border border-slate-800 rounded-xl p-3 flex items-center gap-3 ${
                f.active === false ? 'opacity-50' : ''
              }`}
            >
              {f.hasLogo ? (
                <img
                  src={`/api/foreninger/${f.slug}/logo`}
                  alt=""
                  className="w-10 h-10 rounded object-contain bg-white p-0.5 shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-slate-800 shrink-0 flex items-center justify-center text-slate-600">
                  —
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">
                  {f.name}
                  {f.shortName && <span className="text-slate-500 font-normal"> ({f.shortName})</span>}
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {f.slug}
                  {f.active === false && ' · deaktivert'}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 text-xs">
                <label className="text-sky-400 hover:text-sky-300 cursor-pointer whitespace-nowrap">
                  {f.hasLogo ? 'Bytt logo' : 'Last opp logo'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        const logo = await fileToLogoDataUrl(file);
                        update.mutate({ id: f.id, data: { logo } });
                      } catch (err) {
                        alert(err instanceof Error ? err.message : 'Kunne ikke lese bildet');
                      }
                    }}
                  />
                </label>
                {f.hasLogo && (
                  <button
                    onClick={() => update.mutate({ id: f.id, data: { logo: null } })}
                    className="text-slate-500 hover:text-slate-300"
                  >
                    fjern
                  </button>
                )}
                {f.active === false ? (
                  <button
                    onClick={() => update.mutate({ id: f.id, data: { active: true } })}
                    className="text-emerald-400"
                  >
                    Aktiver
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (confirm(`Deaktivere ${f.name}?`)) del.mutate(f.id);
                    }}
                    className="text-red-300"
                  >
                    Deaktiver
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Les en bildefil og skaler ned til maks `max` px (PNG data-URL). Holder DB liten. */
function fileToLogoDataUrl(file: File, max = 200): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Kunne ikke lese filen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Ugyldig bildefil'));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas ikke støttet'));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}
